import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';
import type { User } from '../types';

interface AuthState {
  token: string | null; user: User | null; loading: boolean; error: string | null;
  signUp: (email: string, password: string, name: string, handle?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void; fetchUser: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  completeOnboarding: (data: any) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null, user: null, loading: false, error: null,
      signUp: async (email, password, name, handle?) => {
        set({ loading: true, error: null });
        try {
          const res = await api.post<{ success: boolean; data: { token: string; user: User } }>('/auth/signup', { email, password, name, handle: handle || undefined });
          set({ token: res.data.token, user: res.data.user, loading: false });
        } catch (err: any) { set({ error: err.message, loading: false }); throw err; }
      },
      signIn: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const res = await api.post<{ success: boolean; data: { token: string; user: User } }>('/auth/signin', { email, password });
          set({ token: res.data.token, user: res.data.user, loading: false });
        } catch (err: any) { set({ error: err.message, loading: false }); throw err; }
      },
      signOut: () => set({ token: null, user: null }),
      fetchUser: async () => {
        try { const res = await api.get<{ success: boolean; data: User }>('/users/me'); set({ user: res.data }); }
        catch { set({ token: null, user: null }); }
      },
      updateProfile: async (data) => {
        set({ loading: true, error: null });
        try { const res = await api.put<{ success: boolean; data: User }>('/users/profile', data); set({ user: res.data, loading: false }); }
        catch (err: any) { set({ error: err.message, loading: false }); throw err; }
      },
      completeOnboarding: async (data) => {
        set({ loading: true, error: null });
        try { const res = await api.post<{ success: boolean; data: User }>('/users/onboarding', data); set({ user: res.data, loading: false }); }
        catch (err: any) { set({ error: err.message, loading: false }); throw err; }
      },
      changePassword: async (currentPassword, newPassword) => {
        set({ loading: true, error: null });
        try { await api.put('/users/change-password', { currentPassword, newPassword }); set({ loading: false }); }
        catch (err: any) { set({ error: err.message, loading: false }); throw err; }
      },
      clearError: () => set({ error: null }),
    }),
    { name: 'auth-storage', partialize: (state) => ({ token: state.token, user: state.user }) }
  )
);
